import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteAttachment,
  downloadAttachment,
  getAttachmentBlobUrl,
  listAttachments,
} from "../../api/attachments";
import { isNative } from "../../utils/mobile";
import "./AttachmentList.css";

function isImage(mimeType = "", name = "") {
  return (
    mimeType.startsWith("image/") ||
    /\.(jpe?g|png)$/i.test(name)
  );
}

function isPdf(mimeType = "", name = "") {
  return mimeType === "application/pdf" || /\.pdf$/i.test(name);
}

function canPreviewInBrowser(mimeType, name) {
  return isImage(mimeType, name) || isPdf(mimeType, name);
}

/**
 * @param {object} props
 * @param {string} props.entityType
 * @param {string} props.entityId
 * @param {boolean} [props.showDelete=true] — hide on approval screens
 * @param {boolean} [props.compact=false]
 * @param {boolean} [props.hideIfEmpty=false] — omit the block when there are no files
 * @param {boolean} [props.lazy=false] — fetch only after the block enters the viewport
 */
export default function AttachmentList({
  entityType,
  entityId,
  showDelete = true,
  compact = false,
  hideIfEmpty = false,
  lazy = false,
  refreshKey = 0,
}) {
  const rootRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(!lazy);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null); // { url, mimeType, name, isPdf }
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!lazy) {
      setShouldLoad(true);
      return undefined;
    }
    setShouldLoad(false);
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "120px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [lazy, entityType, entityId]);

  const load = useCallback(async () => {
    if (!entityType || !entityId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await listAttachments(entityType, entityId);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر جلب المرفقات",
      );
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (!shouldLoad) return;
    load();
  }, [load, refreshKey, shouldLoad]);

  useEffect(() => {
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  async function handleView(item) {
    setBusyId(item.id);
    setError("");
    try {
      // Android WebView blanks PDFs and often can't open Office files in-app —
      // hand off to the native share/open sheet instead.
      if (
        isNative() &&
        (isPdf(item.mimeType, item.originalName) ||
          !isImage(item.mimeType, item.originalName))
      ) {
        await downloadAttachment(item.id, item.originalName);
        return;
      }

      const url = await getAttachmentBlobUrl(item.id);
      if (preview?.url) URL.revokeObjectURL(preview.url);
      setPreview({
        url,
        mimeType: item.mimeType,
        name: item.originalName,
        isPdf: isPdf(item.mimeType, item.originalName),
      });
    } catch (err) {
      setError(err.message || "تعذر عرض الملف");
    } finally {
      setBusyId(null);
    }
  }

  async function handleDownload(item) {
    setBusyId(item.id);
    setError("");
    try {
      await downloadAttachment(item.id, item.originalName);
    } catch (err) {
      setError(err.message || "تعذر تحميل الملف");
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(item) {
    if (!window.confirm(`حذف المرفق «${item.originalName}»؟`)) return;
    setBusyId(item.id);
    try {
      await deleteAttachment(item.id);
      setItems((prev) => prev.filter((row) => row.id !== item.id));
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر حذف المرفق",
      );
    } finally {
      setBusyId(null);
    }
  }

  function closePreview() {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  if (!entityId) return null;

  if (lazy && !shouldLoad) {
    return <div ref={rootRef} className="att-list-lazy-sentinel" aria-hidden />;
  }

  // hideIfEmpty: stay silent while empty (including during fetch) so list pages
  // don't flash "المرفقات" on every card that has no files.
  if (hideIfEmpty && items.length === 0) {
    return lazy && loading ? (
      <div ref={rootRef} className="att-list-lazy-sentinel" aria-hidden />
    ) : null;
  }

  return (
    <div
      ref={rootRef}
      className={`att-list ${compact ? "is-compact" : ""}`}
      dir="rtl"
    >
      {!compact && (
        <div className="att-list-header">
          <h4 className="att-list-title">المرفقات</h4>
          {loading && <span className="att-list-loading">جاري التحميل…</span>}
        </div>
      )}

      {error && <p className="att-list-error">{error}</p>}

      {!loading && items.length === 0 && (
        <p className="att-list-empty">لا توجد مرفقات</p>
      )}

      <ul className="att-list-items">
        {items.map((item) => {
          const previewable = canPreviewInBrowser(
            item.mimeType,
            item.originalName,
          );
          const allowDelete = showDelete && item.canDelete;
          return (
            <li key={item.id} className="att-list-item">
              <span className="att-list-icon" aria-hidden>
                📎
              </span>
              <span className="att-list-name" title={item.originalName}>
                {item.originalName}
              </span>
              <div className="att-list-actions">
                {previewable && (
                  <button
                    type="button"
                    className="att-list-btn"
                    disabled={busyId === item.id}
                    onClick={() => handleView(item)}
                  >
                    عرض
                  </button>
                )}
                <button
                  type="button"
                  className="att-list-btn"
                  disabled={busyId === item.id}
                  onClick={() => handleDownload(item)}
                >
                  تحميل
                </button>
                {allowDelete && (
                  <button
                    type="button"
                    className="att-list-btn att-list-btn-danger"
                    disabled={busyId === item.id}
                    onClick={() => handleDelete(item)}
                  >
                    حذف
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {preview && (
        <div className="att-lightbox" role="dialog" aria-modal="true">
          <div className="att-lightbox-backdrop" onClick={closePreview} />
          <div className="att-lightbox-panel">
            <div className="att-lightbox-header">
              <span>{preview.name}</span>
              <button type="button" onClick={closePreview}>
                إغلاق
              </button>
            </div>
            <div className="att-lightbox-body">
              {preview.isPdf ? (
                <iframe
                  title={preview.name}
                  src={preview.url}
                  className="att-lightbox-iframe"
                />
              ) : (
                <img
                  src={preview.url}
                  alt={preview.name}
                  className="att-lightbox-img"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
